/**
 * Sortable, sticky column header. Adapted from
 * reporting_app/web/src/components/ui/SortableHeader.tsx: that version owns
 * its own `SortState<K>`/`nextSort()` pair, a second sort abstraction
 * alongside `useTableSort`'s. DRA has one sort story — this reads
 * `useTableSort`'s `SortState` ({key, dir}) directly instead.
 *
 * The cell carries `sticky top-0` itself, so a table only needs a scroll
 * container with a bounded height for the header to stay put. Until P37 no DRA
 * table had one, and the header did not stick anywhere (audit S1-08).
 *
 * =========================================================================
 * THE HEADER IS A BUTTON NOW (P37 — audit S1-04, WCAG 2.1.1 / 4.1.2)
 * =========================================================================
 * It used to be `<th onClick>`: measured live, `tabIndex -1`, `role` null,
 * `aria-sort` null, no key handler — sorting was mouse-only on every table in
 * the app. The click moved onto a real `<button>` that fills the cell, and the
 * `<th>` states the current order in `aria-sort`, which is the half a screen
 * reader needs and no amount of styling provides.
 *
 * The button does NOT wrap the funnel: a filter menu inside a sort button is a
 * control inside a control, and the browser would have to guess which one a
 * press means. They are siblings in one 36 px band (reference §9), and the
 * three click targets that share this cell keep the three guards they had —
 * the row click navigates, the header button sorts, the popover does neither
 * (`stopPropagation` inside `ColumnFilter`), and a finished drag swallows
 * exactly one click (`shouldSuppressClick` in `useResizableColumns`).
 *
 * =========================================================================
 * WHY THE CHEVRONS ARE NOT ALWAYS DRAWN
 * =========================================================================
 * Two 10 px chevrons plus their gap cost 16 px of inline width in every header,
 * and a filterable column already spends 32 px on its funnel and 24 on padding.
 * Measured at 1280 px, that chrome is 60–76 px of a ~120 px column, which is
 * what made P37's columns unreadable before the label got room. The order is
 * stated in `aria-sort` for a machine and drawn for a person on the column that
 * is actually sorted — plus on hover and on keyboard focus, so the affordance is
 * still discoverable where a reader is looking.
 *
 * `position: relative` is deliberately NOT on this cell any more. `design.md`
 * §9 names it as a trap — on the same rule as `sticky` it cancels it — and only
 * Tailwind's emission order (`.sticky` after `.relative`) was keeping that from
 * biting. A sticky box is itself a containing block for an absolutely positioned
 * child, so the resize handle needs nothing extra.
 */
import type { ReactNode } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { ColumnResizeHandle } from './ColumnResizeHandle';
import { FOCUS_RING_TIGHT } from './focus';
import type { SortState } from '../../hooks/useTableSort';

interface Props {
    label: string;
    sortKey: string;
    sort: SortState;
    onSort: (key: string) => void;
    align?: 'left' | 'right';
    className?: string;
    /** Resizing — `onResizeStart` and `onResetWidth` go together, or neither. */
    onResizeStart?: (clientX: number) => void;
    onResetWidth?: () => void;
    /** Keyboard resizing (P37). With `width`, the handle becomes focusable. */
    onNudgeWidth?: (delta: number) => void;
    width?: number;
    isResizing?: boolean;
    /** A `<ColumnFilter>` (or anything else) rendered next to the label. */
    filter?: ReactNode;
}

export function SortableHeader({
    label, sortKey, sort, onSort, align = 'left', className = '',
    onResizeStart, onResetWidth, onNudgeWidth, width, isResizing = false, filter,
}: Props) {
    const active = sort.key === sortKey;
    // `group-hover` / `group-focus-within` on the cell, so the pair appears while
    // a reader is on the column and stays put on the one that is sorted.
    const chevronClass = (dir: 'asc' | 'desc') =>
        active && sort.dir === dir ? 'text-ink' : 'text-faint';

    return (
        <th
            scope="col"
            aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
            // `py-0` is load-bearing: a table cell keeps the UA's `padding: 1px`
            // unless something says otherwise, and `px-3` only sets the sides —
            // measured, the band came out 38.5 px against the reference's 36.
            className={`group sticky top-0 z-10 h-ctl select-none bg-head px-3 py-0 text-xs font-bold uppercase tracking-[.04em] text-muted ${
                align === 'right' ? 'text-right' : 'text-left'
            } ${className}`}
        >
            <span className={`flex h-ctl items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
                <button
                    type="button"
                    onClick={() => onSort(sortKey)}
                    // The full name, because the column is often narrower than its
                    // own heading and the heading then truncates (P37, §17).
                    title={`${label} — sort by this column`}
                    // `h-full`: the button is the whole 36 px band, not the 16 px
                    // line of text inside it (reference §4 — no target under 32 px).
                    className={`inline-flex h-full min-w-0 flex-1 items-center gap-1 rounded-ctl uppercase tracking-[.04em] hover:text-ink ${
                        align === 'right' ? 'flex-row-reverse text-right' : 'text-left'
                    } ${FOCUS_RING_TIGHT}`}
                >
                    <span className="truncate">{label}</span>
                    {/* `hidden`, not `opacity-0`: an invisible box still costs its
                        16 px of inline width, which is the whole reason the pair
                        is conditional. */}
                    <span
                        className={`shrink-0 flex-col -space-y-1 ${
                            active ? 'inline-flex' : 'hidden group-hover:inline-flex group-focus-within:inline-flex'
                        }`}
                        aria-hidden="true"
                    >
                        <ChevronUp size={10} className={chevronClass('asc')} />
                        <ChevronDown size={10} className={chevronClass('desc')} />
                    </span>
                </button>
                {filter}
            </span>
            {onResizeStart && onResetWidth && (
                <ColumnResizeHandle
                    label={label}
                    active={isResizing}
                    onStart={onResizeStart}
                    onReset={onResetWidth}
                    width={width}
                    onNudge={onNudgeWidth}
                />
            )}
        </th>
    );
}
